import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Section, Cell, Caption } from '@telegram-apps/telegram-ui'
import { Plus } from 'lucide-react'

import { api } from '../../lib/api'
import type { Task, Me } from '../../lib/types'
import PageHeader from '../../components/PageHeader'
import TaskCard from '../../components/TaskCard'
import { SkeletonList } from '../../components/Skeleton'

type AssigneeFilter = 'all' | 'me' | 'secretary' | 'driver'
type StatusFilter = 'pending' | 'done'

export default function DirectorTasks() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const me = qc.getQueryData<Me>(['me'])
  const secName = me?.secretary_name || 'Анастасия'
  const drvName = me?.driver_name || 'Владимир'
  const [assignee, setAssignee] = useState<AssigneeFilter>('all')
  const [status, setStatus] = useState<StatusFilter>('pending')

  const { data, isLoading } = useQuery<Task[]>({
    queryKey: ['tasks', assignee, status],
    queryFn: () => api.get<Task[]>(`/tasks?assignee=${assignee}&status=${status}`),
  })

  const markDone = useMutation({
    mutationFn: (taskId: number) => api.post(`/tasks/${taskId}/done`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })

  const myId = me?.user_id

  const assigneeChips: { id: AssigneeFilter; label: string }[] = [
    { id: 'all',       label: 'Все' },
    { id: 'me',        label: 'Я' },
    { id: 'secretary', label: secName },
    { id: 'driver',    label: drvName },
  ]

  return (
    <>
      <PageHeader title="Задачи" />

      <div className="chip-row">
        {assigneeChips.map(({ id, label }) => (
          <button
            key={id}
            className={`chip${assignee === id ? ' is-active' : ''}`}
            onClick={() => setAssignee(id)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="seg-row">
        <button
          className={`seg-btn${status === 'pending' ? ' is-active' : ''}`}
          onClick={() => setStatus('pending')}
        >
          Активные
        </button>
        <button
          className={`seg-btn${status === 'done' ? ' is-active' : ''}`}
          onClick={() => setStatus('done')}
        >
          Выполненные
        </button>
      </div>

      {isLoading ? (
        <SkeletonList count={6} />
      ) : !data || data.length === 0 ? (
        <Section><Cell><Caption style={{ opacity: 0.6 }}>Пусто</Caption></Cell></Section>
      ) : (
        <div>
          {data.map((t) => (
            <TaskCard
              key={t.id}
              task={t}
              canCheck={status === 'pending' && t.assignee_id === myId}
              onToggleDone={(id) => markDone.mutate(id)}
              detailPath={`/tasks/${t.id}`}
            />
          ))}
        </div>
      )}

      <button className="fab-pill" onClick={() => navigate('/tasks/new')}>
        <Plus size={16} strokeWidth={2.5} />
        Новая
      </button>
    </>
  )
}
